import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export type DateInput = string | number | Date | Dayjs;
export type TimezoneInput = number | string | undefined;

export const systemDate = (...args: Parameters<typeof dayjs>) => {
  return dayjs(...args).tz('Asia/Taipei');
};

export const getDate = (date: DateInput, timezone?: TimezoneInput) => {
  if (timezone === 'system') {
    return systemDate(date);
  }

  if (typeof timezone === 'number') {
    return dayjs(date).utcOffset(timezone);
  }

  if (typeof timezone === 'string') {
    // 自動處理夏令時，更容易理解和維護，符合最佳實踐
    return dayjs(date).tz(timezone);
  }

  return dayjs(date);
};

export const systemNow = () => {
  return systemDate();
};

export const getTimezoneOffset = () => {
  return -(new Date().getTimezoneOffset() / 60);
};

export const format = (
  date: DateInput,
  pattern = 'MM/DD/YYYY HH:mm:ss',
  timezone?: TimezoneInput
) => {
  return getDate(date, timezone).format(pattern);
};

export const isSameDay = (
  date1: DateInput,
  date2: DateInput,
  timezone?: TimezoneInput
) => {
  return getDate(date1, timezone).isSame(getDate(date2, timezone), 'date');
};

export const isSameWeek = (
  date1: DateInput,
  date2: DateInput,
  timezone?: TimezoneInput
) => {
  return getDate(date1, timezone).isSame(getDate(date2, timezone), 'week');
};

export const isYesterday = (date: DateInput, timezone?: TimezoneInput) => {
  return isSameDay(date, systemNow().subtract(1, 'day'), timezone);
};

export const isAfter = (
  date1: DateInput,
  date2: DateInput,
  timezone?: TimezoneInput
) => {
  return getDate(date1, timezone).isAfter(getDate(date2, timezone));
};

export const isBefore = (
  date1: DateInput,
  date2: DateInput,
  timezone?: TimezoneInput
) => {
  return getDate(date1, timezone).isBefore(getDate(date2, timezone));
};

export const endOfDay = (date: DateInput, timezone?: TimezoneInput) => {
  return getDate(date, timezone).endOf('day').format('MM/DD/YYYY, HH:mm:ss');
};

export const diffDays = (
  date1: DateInput,
  date2: DateInput,
  timezone?: TimezoneInput
) => {
  return getDate(date1, timezone)
    .startOf('day')
    .diff(getDate(date2, timezone).startOf('day'), 'day');
};

export const isToday = (date: DateInput, timezone?: TimezoneInput) => {
  return isSameDay(systemNow().toDate(), date, timezone);
};

export default dayjs;
